// Admin types for referral management and analytics

export interface ReferralStats {
  account_created: number;
  agent_created: number;
  channel_connected: number;
  subscription_created: number;
  rejected: number;
}

export interface TopReferrer {
  _id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  verified: boolean;
  status: string;
  allow_referral: boolean;
  referral_code?: string;
  created_at: string;
  updated_at: string;
  effective_referrals_count: number;
  total_referrals_count: number;
  referral_stats: ReferralStats;
}

export interface RefereeDetails {
  first_name?: string;
  last_name?: string;
  email?: string;
  status: string;
  verified: boolean;
}

export interface RejectionDetails {
  reason: string;
  timestamp: string;
}

export interface ReferralDetail {
  _id: string;
  referrer_owner_id: string;
  referee_owner_id: string;
  agent_id?: string;
  status: string;
  created_at: string;
  updated_at: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  rejected?: RejectionDetails;
  referee_details?: RefereeDetails;
}

export interface AdminPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ListTopReferrersData {
  referrers: TopReferrer[];
  pagination: AdminPagination;
  dateRange: DateRange;
}

export interface GetReferralsByReferrerData {
  referrals: ReferralDetail[];
  pagination: AdminPagination;
  referrerInfo: {
    referrer_owner_id: string;
  };
}

export interface AdminResponse<T> {
  hasError: boolean;
  message: string;
  data: T | null;
}

export type ListTopReferrersResponse = AdminResponse<ListTopReferrersData>;
export type GetReferralsByReferrerResponse =
  AdminResponse<GetReferralsByReferrerData>;

// Referral status enum for filtering and display
export enum ReferralStatus {
  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  AGENT_CREATED = 'AGENT_CREATED',
  CHANNEL_CONNECTED = 'CHANNEL_CONNECTED',
  SUBSCRIPTION_CREATED = 'SUBSCRIPTION_CREATED',
  REJECTED = 'REJECTED',
}

// Date range presets for quick filtering
export interface DateRangePreset {
  label: string;
  startDate: Date;
  endDate: Date;
}

// Owner management types
export interface AdminOwner {
  _id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  verified: boolean;
  status: string;
  allow_referral: boolean;
  referral_code?: string;
  created_at: string;
  updated_at: string;
}

export interface ListOwnersData {
  owners: AdminOwner[];
  pagination: AdminPagination;
}

export interface SearchOwnersData {
  owners: AdminOwner[];
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
}

export interface ActivateReferralData {
  owner_id: string;
  allow_referral: boolean;
  referral_code?: string;
  activated_at: string;
}

export type ListOwnersResponse = AdminResponse<ListOwnersData>;
export type SearchOwnersResponse = AdminResponse<SearchOwnersData>;
export type ActivateReferralResponse = AdminResponse<ActivateReferralData>;
